import { createListenerMiddleware } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import {
  blackFrameDetected,
  solidFrameDetected,
  videoDetected,
  selectRemoteMediaDetections,
  clearAllDetections,
  videoNotDetected,
} from './remoteMediaDetectionsSlice';

import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { setDeafStatus } from 'features/call/call-deaf/store';
import type { RootState } from 'features/app/store/store';
import { formatIsoDate } from 'common/helpers';

const createRateLimiter = (intervalMs: number) => {
  let lastSentAt = 0;
  return {
    shouldSend: () => {
      const now = Date.now();
      const should = lastSentAt === 0 || now - lastSentAt >= intervalMs;
      if (should) {
        lastSentAt = now;
      }
      return should;
    },
    reset: () => {
      lastSentAt = 0;
    },
  };
};

// Rate limit intervals (ms)
// Report frame anomalies (black/solid frames) at most once every 30 seconds
const FRAME_ANOMALY_RATE_LIMIT_MS = 30000;
// Report video rendering status changes at most once every minute
const VIDEO_RENDERING_STATUS_RATE_LIMIT_MS = 60000;

const listener = createListenerMiddleware();

// Create rate limiters for each detection type
const blackFrameLimiter = createRateLimiter(FRAME_ANOMALY_RATE_LIMIT_MS);
const solidFrameLimiter = createRateLimiter(FRAME_ANOMALY_RATE_LIMIT_MS);
const videoDetectedLimiter = createRateLimiter(
  VIDEO_RENDERING_STATUS_RATE_LIMIT_MS
);
const videoNotDetectedLimiter = createRateLimiter(
  VIDEO_RENDERING_STATUS_RATE_LIMIT_MS
);

// Black frame detection handler
listener.startListening({
  actionCreator: blackFrameDetected,
  effect: async (action: PayloadAction<any>, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const detections = selectRemoteMediaDetections(state);

    if (detections.lastBlackScreenPayload && blackFrameLimiter.shouldSend()) {
      const { meanLuminance, stdDev } = detections.lastBlackScreenPayload;
      listenerApi.dispatch(
        sendAnalyticsInfo({
          Method: 'VideoFrameDetector',
          Event: 'BlackFrameDetected',
          LogGroup: 'VideoFrameDetection',
          name: 'Black Frame Detected',
          meanLuminance,
          stdDev,
        }) as any
      );
    }
  },
});

// Solid frame detection handler
listener.startListening({
  actionCreator: solidFrameDetected,
  effect: async (action: PayloadAction<any>, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const detections = selectRemoteMediaDetections(state);

    if (detections.lastSolidScreenPayload && solidFrameLimiter.shouldSend()) {
      const { meanLuminance, stdDev, dominantColor } =
        detections.lastSolidScreenPayload;

      listenerApi.dispatch(
        sendAnalyticsInfo({
          Method: 'VideoFrameDetector',
          LogGroup: 'VideoFrameDetection',
          Event: 'SolidFrameDetected',
          name: 'Solid Frame Detected',
          meanLuminance,
          stdDev,
          dominantColor,
        }) as any
      );
    }
  },
});

// Video not detected handler
listener.startListening({
  actionCreator: videoNotDetected,
  effect: async (action: PayloadAction<any>, listenerApi) => {
    if (videoNotDetectedLimiter.shouldSend()) {
      listenerApi.dispatch(
        sendAnalyticsInfo({
          Method: 'VideoFrameDetector',
          Event: 'VideoRenderingStatus',
          name: 'Video Rendering Status',
          VideoDetected: false,
          LogGroup: 'VideoFrameDetection',
        }) as any
      );
    }
  },
});

// Video detected handler
listener.startListening({
  actionCreator: videoDetected,
  effect: async (action: PayloadAction<any>, listenerApi) => {
    if (videoDetectedLimiter.shouldSend()) {
      listenerApi.dispatch(
        sendAnalyticsInfo({
          Method: 'VideoFrameDetector',
          Event: 'VideoRenderingStatus',
          name: 'Video Rendering Status',
          VideoDetected: true,
          LogGroup: 'VideoFrameDetection',
        }) as any
      );
    }
  },
});

// When deaf disconnects - send report if no video was detected during the entire
// connection period.
listener.startListening({
  actionCreator: setDeafStatus,

  effect: async (action: PayloadAction<any>, listenerApi) => {
    if (action.payload === 'disconnected') {
      // Re-check the detections slice to see if we previously recorded a
      // deaf connection time. If we never recorded one, skip reporting.
      const state = listenerApi.getState() as RootState;
      const detections = selectRemoteMediaDetections(state);

      if (!detections.deafConnectedAt) {
        return;
      }

      // If we observed no video, or never observed any sample,
      // report a "no video" event on disconnect.
      if (detections.videoWasAtSomePointDetected === false) {
        const eventPayload: Record<string, any> = {
          Name: 'NoVideoForDurationDeafConnection',
          Timestamp: new Date().toISOString(),
          videoDetected: false,
          LogGroup: 'VideoFrameDetection',
          deafConnectedAt: formatIsoDate(detections.deafConnectedAt),
          deafDisconnectedAt: new Date().toISOString(),
        };

        listenerApi.dispatch(
          sendAnalyticsInfo({
            Method: 'NoVideoDetectedForDeafParticipant',
            Message:
              "No video detected for the duration of the deaf participant's connection",
            Event: eventPayload,
            LogGroup: 'VideoFrameDetection',
          }) as any
        );
      }

      // Clear detection state after handling the disconnect so future calls
      // start with a clean slate.
      listenerApi.dispatch(clearAllDetections());
    }
  },
});

export const remoteMediaListenerMiddleware = listener.middleware;
